/**
 * ImageLightbox — a plain, dark full-screen image viewer.
 *
 * The picture centred on a dimmed backdrop, a close button, and prev/next when
 * there is more than one. Deliberately simple — no zoom/rotate/copy chrome — so a
 * produced image opens the same quiet way whether it is clicked in a transcript
 * or in a side rail. Portalled to the body so it fills the window rather than a
 * transformed canvas plane.
 */
import * as React from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'

export interface ImageLightboxItem {
  src: string
  /** A directly-renderable low-res source (a thumbnail already in memory) shown
   *  the instant the viewer opens, while `loadDataUrl(src)` fetches the full one.
   *  Without it a side-rail thumbnail — which only holds the small version — has
   *  to load the full image on click, so the viewer opened with a delay. */
  poster?: string
}

export interface ImageLightboxProps {
  isOpen: boolean
  onClose: () => void
  items: ImageLightboxItem[]
  initialIndex?: number
  /** Resolve an item's `src` to something an `<img>` can render — a data URL, a
   *  path the host loads full-res, or the src itself. */
  loadDataUrl: (src: string) => Promise<string>
}

export function ImageLightbox({ isOpen, onClose, items, initialIndex = 0, loadDataUrl }: ImageLightboxProps) {
  const { t } = useTranslation()
  const [index, setIndex] = React.useState(initialIndex)
  const [dataUrl, setDataUrl] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (isOpen) setIndex(initialIndex)
  }, [initialIndex, isOpen])

  React.useEffect(() => {
    if (!isOpen) return
    const item = items[index]
    if (!item) return
    let stale = false
    setDataUrl(null)
    void (async () => {
      try {
        const url = await loadDataUrl(item.src)
        if (!stale) setDataUrl(url)
      } catch {
        if (!stale) setDataUrl(null)
      }
    })()
    return () => {
      stale = true
    }
  }, [isOpen, index, items, loadDataUrl])

  const step = React.useCallback(
    (delta: number) => {
      setIndex((i) => {
        const next = i + delta
        return next < 0 || next >= items.length ? i : next
      })
    },
    [items.length],
  )

  React.useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowRight') step(1)
      else if (e.key === 'ArrowLeft') step(-1)
      else return
      e.preventDefault()
      e.stopPropagation()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [isOpen, onClose, step])

  if (!isOpen || items.length === 0) return null

  return createPortal(
    <div
      data-air-overlay="true"
      className="animate-in fade-in-0 fixed inset-0 z-overlay flex flex-col items-center justify-center gap-4 bg-black/80 p-8 backdrop-blur-sm duration-200"
      onClick={(e) => {
        e.stopPropagation()
        onClose()
      }}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onClose()
        }}
        aria-label={t('common.close')}
        className="absolute right-4 top-4 rounded-lg p-2 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
      >
        <X className="h-5 w-5" />
      </button>
      {items.length > 1 && (
        <>
          <button
            type="button"
            disabled={index === 0}
            onClick={(e) => {
              e.stopPropagation()
              step(-1)
            }}
            aria-label={t('air.screenshots.previous')}
            className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full p-3 text-white/70 transition-colors hover:bg-white/10 hover:text-white disabled:pointer-events-none disabled:opacity-20"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            type="button"
            disabled={index === items.length - 1}
            onClick={(e) => {
              e.stopPropagation()
              step(1)
            }}
            aria-label={t('air.screenshots.next')}
            className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full p-3 text-white/70 transition-colors hover:bg-white/10 hover:text-white disabled:pointer-events-none disabled:opacity-20"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}
      {(dataUrl ?? items[index]?.poster) && (
        <img
          key={index}
          // The full image once loaded; until then the poster (a thumbnail already
          // in memory), so the viewer opens instantly and then sharpens.
          src={dataUrl ?? items[index]!.poster}
          alt=""
          className="animate-in fade-in-0 zoom-in-95 max-h-[85vh] max-w-[85vw] rounded-lg object-contain shadow-strong duration-200"
          onClick={(e) => e.stopPropagation()}
          draggable={false}
        />
      )}
      {items.length > 1 && (
        <span className="text-xs tabular-nums text-white/60">
          {index + 1} / {items.length}
        </span>
      )}
    </div>,
    document.body,
  )
}
